/**
 * Zobyda · Find your shelf, IMAGE-style (31 Oct 2026), premium pass (3 Nov 2026), shade faces (3 Nov 2026),
 * shade asked after "My usual makeup" too (4 Nov 2026)
 * One question per screen: 11 steps, 12 when the retinoid follow-up applies, plus "Which skin tone is
 * closest to yours?" after "A hint of tint" or "My usual makeup" (7 faces, one per Tinted shade, then
 * "Deeper than these" and "Not sure"). Her face answer ticks her shade on the result and sets The Full
 * Edit's tint shade; "Deeper than these" leaves the tint off her shelf, kindly. After "My usual makeup"
 * the tint stays off her shelf as before: her shade waits for The Full Edit, or for the tint if she
 * ticks it herself. Then "Your shelf",
 * sized by her answer to "How many steps would you like?": two (cleanse and moisturize), three or
 * four, or the full shelf (the basics for a full morning and evening plus everything her answers
 * point to). In routine order, each with a line from its own product page. Then "The rest of the shelf" (every other
 * product, unticked, so anything can be added), How to start and The Full Edit. Adds go through
 * the cart drawer like the product pages (zb-pdp.js). Markup: sections/zb-shelf-quiz.liquid.
 *
 * Her data:
 * - Klaviyo: only if she gives her email and ticks the box, and only on the live theme. A preview
 *   logs what would be sent to the console and sends nothing.
 * - Shopify: her answers go on the cart as two attributes when she adds from her result.
 * - Meta-safe signals through Shopify.analytics.publish: zb_quiz_started, zb_quiz_finished
 *   (count, value), zb_quiz_lead, zb_quiz_add (count, value). Never an answer. Nothing after
 *   "Teens" is chosen.
 */
(function () {
  'use strict';

  var CO = 'cleansing-oil';
  var NIA = 'niacinamide-vitamin-boost-serum';
  var OF = 'oil-free-daily-moisturizer';
  var TINT = 'tinted-moisturizer';
  var MR = 'makeup-remover-solution';
  var HA = 'hyaluronic-acid-serum';
  var NR = 'night-renewal-creme';
  var LB = 'vegan-lip-balm';

  /* Routine order and step labels, as set on the product pages */
  var ORDER = [
    { h: CO, part: 'am', step: 'Morning · step 1' },
    { h: NIA, part: 'am', step: 'Morning · step 2' },
    { h: OF, part: 'am', step: 'Morning · step 3' },
    { h: TINT, part: 'am', step: 'Morning · optional' },
    { h: MR, part: 'pm', step: 'Evening · step 1' },
    { h: HA, part: 'pm', step: 'Evening · step 2' },
    { h: NR, part: 'pm', step: 'Evening · step 3' },
    { h: LB, part: 'any', step: 'Whenever you like' }
  ];
  var EVENING = [MR, HA, NR];

  /* One line per product, in its own page's words. Nothing new is claimed. */
  var LINE = {};
  LINE[CO] = {
    oil: 'Clears the night off: overnight oil, leftover night cream, the film that settles by morning.',
    base: 'Clears the night off, so skin is ready for serum.'
  };
  LINE[NIA] = {
    pores: 'Pores that look smaller, redness that looks calmer.',
    calm: 'Redness that looks calmer, a barrier that feels sturdier.',
    base: 'The quiet all-rounder: pores that look smaller, redness that looks calmer.'
  };
  LINE[OF] = {
    shine: 'Featherlight, oil-free and non-comedogenic, so it doesn’t sit heavy.',
    tint: 'Lightweight moisture that sits neatly under tint.',
    base: 'Featherlight moisture you forget you’re wearing.'
  };
  LINE[TINT] = {
    tone: 'Evens skin tone without hiding it.',
    light: 'Light coverage with built-in hydration.'
  };
  LINE[MR] = {
    makeup: 'Lifts makeup, sunscreen and the day off, without leaving skin tight.',
    base: 'The honest first step of any evening: lifts sunscreen and the day off.'
  };
  LINE[HA] = { dry: 'Draws moisture in and holds it close. A few drops on damp skin.' };
  LINE[NR] = {
    lines: 'Glycolic acid and a retinoid, the two that do most of the work on dullness, dehydration and fine lines. Wear SPF the next day.',
    bright: 'Glycolic acid and a retinoid, the two that do most of the work on dullness. Wear SPF the next day.',
    lid: 'Goes straight over the serum to seal it in: hyaluronic acid holds water, the cream is the lid. Wear SPF the next day.',
    gentle: 'Glycolic acid and a retinoid. It trades gentleness for results and isn’t a sensitive-skin product, so we haven’t ticked it. Your call: patch-test first, and wear SPF the next day.'
  };
  LINE[LB] = { lips: 'Nourishes and softens. As often as you like.' };

  /* The line a product gets when it's there as one of the basics */
  var FALLBACK = {};
  FALLBACK[CO] = 'base';
  FALLBACK[NIA] = 'base';
  FALLBACK[OF] = 'base';
  FALLBACK[TINT] = 'light';
  FALLBACK[MR] = 'base';
  FALLBACK[HA] = 'dry';
  FALLBACK[NR] = 'lines';
  FALLBACK[LB] = 'lips';

  /* What it's for, when no answer pointed to it */
  var ROLE = {};
  ROLE[CO] = 'Your first step, every morning';
  ROLE[NIA] = 'The quiet all-rounder';
  ROLE[OF] = 'Your morning moisturizer';
  ROLE[TINT] = 'For a hint of tint';
  ROLE[MR] = 'Your first step, every evening';
  ROLE[HA] = 'For deep hydration';
  ROLE[NR] = 'The last step of the evening';
  ROLE[LB] = 'The small useful one';

  var TAG = {
    concerns: {
      lines: 'fine lines',
      dry: 'dryness',
      tone: 'uneven tone',
      pores: 'pores and shine',
      redness: 'redness',
      lips: 'dry lips',
      keep: 'keeping it healthy'
    },
    type: { dry: 'dry skin', combo: 'combination skin', oily: 'oily skin', normal: 'normal skin' },
    goal: {
      glow: 'soft, hydrated, glowy',
      clear: 'clear and fresh-looking',
      even: 'a brighter, more even look',
      lines: 'softer-looking fine lines',
      calm: 'sturdier and calmer'
    },
    finish: { bare: 'something weightless', tint: 'a hint of tint', makeup: 'your usual makeup' },
    climate: {
      temperate: 'a temperate climate',
      dry: 'a hot, dry climate',
      humid: 'a humid climate',
      mountain: 'a mountain climate'
    },
    steps: { two: 'two steps', few: 'three or four steps', full: 'the full shelf' }
  };

  var GOAL = {
    glow: 'For soft, hydrated, glowy skin.',
    clear: 'For clear, fresh-looking skin.',
    even: 'For a brighter, more even look.',
    lines: 'For softer-looking fine lines.',
    calm: 'For skin that feels sturdier and calmer.'
  };

  /* Tinted shades no longer sold: kept out of the shade pictures and The Full Edit's picker */
  var GONE = ['Buttercream', 'Espresso'];
  /* Light to deep, as on Blanka's swatches and the faces. Any shade not listed follows, in The Full Edit's order. */
  var SHADES = ['Porcelain', 'Seashell', 'Sandstone', 'Mile Beach', 'Tan', 'Toasted', 'Maple'];
  /* Close in depth on Blanka's swatches, so they differ mainly by undertone: the jewellery test helps */
  var DEEP_THREE = ['Tan', 'Toasted', 'Maple'];
  var TIP = 'If gold suits you best, try Toasted or Maple. If gold and silver both do, Tan.';
  var NO_TINT = 'Our tint doesn’t go deeper than Maple yet, so we’ve left it off your shelf.';

  var SEQ = ['concerns', 'type', 'goal', 'sensitive', 'age', 'retinoid', 'using', 'why', 'finish', 'skintone', 'climate', 'steps', 'email'];
  var MULTI = { concerns: true };
  var STORE_KEY = 'zbq-shelf-v3';
  var KLAVIYO_EVENT = 'Finished Find your shelf';
  var KLAVIYO_REVISION = '2026-01-15';

  var SUN =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" aria-hidden="true"><circle cx="8" cy="8" r="3"/><path d="M8 1.5v1.6M8 12.9v1.6M1.5 8h1.6M12.9 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M3.4 12.6l1.1-1.1M11.5 4.5l1.1-1.1"/></svg>';
  var MOON =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" aria-hidden="true"><path d="M13 9.6A5.5 5.5 0 0 1 6.4 3a5.5 5.5 0 1 0 6.6 6.6z"/></svg>';
  var DOT =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><circle cx="8" cy="8" r="4.5"/><path d="M5.6 8h4.8"/></svg>';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function has(list, value) {
    return !!list && list.indexOf(value) !== -1;
  }

  function esc(text) {
    return String(text == null ? '' : text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function cap(text) {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
  }

  function indexOf(handle) {
    for (var i = 0; i < ORDER.length; i++) if (ORDER[i].h === handle) return i;
    return 99;
  }

  function byRoutine(a, b) {
    return indexOf(a) - indexOf(b);
  }

  function noop() {}

  function save(value) {
    try {
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify(value));
    } catch (error) {
      /* private mode: the quiz still works, it just won't remember */
    }
  }

  function load() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function forget() {
    try {
      window.sessionStorage.removeItem(STORE_KEY);
    } catch (error) {
      /* nothing to forget */
    }
  }

  function publish(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, payload || {});
      }
    } catch (error) {
      /* analytics is optional */
    }
  }

  /* Only the published theme sends anything to Klaviyo; previews and the editor never do */
  function isLive() {
    var s = window.Shopify || {};
    return !!(s.theme && s.theme.role === 'main') && !s.designMode;
  }

  function abs(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    if (url.charAt(0) === '/') return window.location.origin + url;
    return url;
  }

  function setup(el) {
    var data;
    try {
      data = JSON.parse(el.querySelector('[data-zbq-data]').textContent);
    } catch (error) {
      return;
    }

    var routine = data.routine || { min: 3, percent: 15 };
    var form = el.querySelector('[data-zbq-form]');
    var resultBox = el.querySelector('[data-zbq-result]');
    var live = el.querySelector('[data-zbq-live]');
    var notes = el.querySelector('[data-zbq-notes]');
    var stepText = el.querySelector('[data-zbq-steptext]');
    var line = el.querySelector('[data-zbq-line]');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var byHandle = {};
    (data.products || []).forEach(function (p) {
      byHandle[p.handle] = p;
    });

    function fresh() {
      return { a: null, picks: [], rest: [], why: {}, tags: {}, nrReason: '', leadEdit: false, noTint: false, shade: '', shadeFrom: '', matchOut: '', name: '', emailed: false, current: 'concerns' };
    }

    var state = fresh();
    var started = false;
    var pointerPick = false;
    var advanceTimer = null;

    function money(cents) {
      var fixed = (Math.round(cents) / 100).toFixed(2);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var format = data.moneyFormat || '${{amount}}';
      return format
        .replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
          if (key === 'amount_no_decimals') return whole;
          if (key === 'amount_with_comma_separator') return parts[0] + ',' + parts[1];
          return whole + '.' + parts[1];
        })
        .replace(/<[^>]*>/g, '');
    }

    function dollars(cents) {
      return Math.round(cents) / 100;
    }

    function available(handle) {
      var p = byHandle[handle];
      return !!(p && p.available);
    }

    /* ---------- Reading her answers ---------- */

    function val(name) {
      var input = form.querySelector('input[name="' + name + '"]:checked');
      return input ? input.value : '';
    }

    function vals(name) {
      var out = [];
      form.querySelectorAll('input[name="' + name + '"]:checked').forEach(function (input) {
        out.push(input.value);
      });
      return out;
    }

    function needsWhy(a) {
      return a.retinoid === 'no' || a.using === 'lapsed';
    }

    /* The face question follows "A hint of tint", and "My usual makeup" too: the tint stays off a
       makeup shelf, but her shade is then ready for The Full Edit, or for the tint if she adds it */
    function asksTone(a) {
      return a.finish === 'tint' || a.finish === 'makeup';
    }

    function readAnswers() {
      var a = {
        concerns: vals('concerns'),
        type: val('type'),
        goal: val('goal'),
        sensitive: val('sensitive'),
        age: val('age'),
        retinoid: val('retinoid'),
        using: val('using'),
        why: '',
        finish: val('finish'),
        skintone: '',
        climate: val('climate'),
        steps: val('steps')
      };
      if (needsWhy(a)) a.why = val('why');
      if (asksTone(a)) a.skintone = val('skintone');
      return a;
    }

    function sequence() {
      var a = readAnswers();
      return SEQ.filter(function (key) {
        if (key === 'why') return needsWhy(a);
        if (key === 'skintone') return asksTone(a);
        return true;
      });
    }

    /* The words she saw, for Klaviyo and her order */
    function answerText(name, value) {
      var input = form.querySelector('input[name="' + name + '"][value="' + value + '"]');
      var label = input && input.closest('label');
      var text = label && (label.querySelector('.zbq-opt__t') || label.querySelector('span'));
      return text ? text.textContent.trim() : value;
    }

    function panel(key) {
      return form.querySelector('[data-zbq-panel="' + key + '"]');
    }

    function answered(key) {
      if (key === 'email') return true;
      if (MULTI[key]) return !!form.querySelector('input[name="' + key + '"]:checked');
      return !!val(key);
    }

    function syncNext(key) {
      var p = panel(key);
      var button = p && p.querySelector('[data-zbq-next]');
      if (button) button.disabled = !answered(key);
    }

    /* ---------- The face ---------- */

    function paintFace() {
      var zones = {};
      form.querySelectorAll('input[name="concerns"]:checked').forEach(function (input) {
        if (input.dataset.zone) zones[input.dataset.zone] = true;
      });
      el.querySelectorAll('[data-zbq-zone]').forEach(function (zone) {
        zone.classList.toggle('is-on', !!zones[zone.getAttribute('data-zbq-zone')]);
      });
      if (!notes) return;
      var picked = vals('concerns').map(function (v) {
        return TAG.concerns[v];
      });
      notes.textContent = picked.length ? picked.join(' · ') : 'Your skin, as you see it';
    }

    function exclusive(input, noneValue) {
      var group = form.querySelectorAll('input[name="' + input.name + '"]');
      if (input.value === noneValue && input.checked) {
        group.forEach(function (other) {
          if (other !== input) other.checked = false;
        });
      } else if (input.checked) {
        group.forEach(function (other) {
          if (other.value === noneValue) other.checked = false;
        });
      }
    }

    /* ---------- Moving between steps ---------- */

    function bringIntoView() {
      var body = el.querySelector('.zbq__body');
      var top = body.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) {
        body.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
    }

    function show(step, quiet) {
      var isResult = step === 'result';
      state.current = step;
      el.setAttribute('data-zbq-step', step);
      form.hidden = isResult;
      resultBox.hidden = !isResult;
      form.querySelectorAll('[data-zbq-panel]').forEach(function (p) {
        p.hidden = p.getAttribute('data-zbq-panel') !== step;
      });
      if (!isResult) {
        var seq = sequence();
        var i = Math.max(0, seq.indexOf(step));
        stepText.textContent = 'Step ' + (i + 1) + ' of ' + seq.length;
        if (line) line.style.width = Math.round(((i + 1) / seq.length) * 100) + '%';
        syncNext(step);
        /* After "My usual makeup", one line says why we ask her skin tone */
        var toneWhy = step === 'skintone' && panel(step).querySelector('[data-zbq-tone-why]');
        if (toneWhy) toneWhy.hidden = val('finish') !== 'makeup';
      }
      if (quiet) return;
      bringIntoView();
      var target = isResult ? resultBox : panel(step).querySelector('.zbq-step__q');
      if (target) target.focus({ preventScroll: true });
      live.textContent = isResult ? 'Your shelf is ready.' : stepText.textContent + '. ' + (target ? target.textContent : '');
    }

    function next() {
      window.clearTimeout(advanceTimer);
      if (state.current === 'email' || state.current === 'result') return;
      if (!answered(state.current)) return;
      var seq = sequence();
      var i = seq.indexOf(state.current);
      if (i === -1) return show(seq[0]);
      if (i < seq.length - 1) show(seq[i + 1]);
    }

    function back() {
      window.clearTimeout(advanceTimer);
      var seq = sequence();
      var i = seq.indexOf(state.current);
      if (i > 0) show(seq[i - 1]);
    }

    form.addEventListener('pointerdown', function (event) {
      pointerPick = !!event.target.closest('.zbq-opt, .zbq-chip, .zbq-tone');
    });

    form.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') pointerPick = false;
    });

    form.addEventListener('change', function (event) {
      var input = event.target;
      if (!started && input.type !== 'text' && input.type !== 'email') {
        started = true;
        publish('zb_quiz_started', {});
      }
      if (input.name === 'concerns') paintFace();
      if (panel(input.name)) syncNext(input.name);
      if (input.type === 'radio' && pointerPick && panel(input.name) && !panel(input.name).hidden) {
        pointerPick = false;
        window.clearTimeout(advanceTimer);
        advanceTimer = window.setTimeout(next, reduce ? 0 : 320);
      }
    });

    /* Clear a mail message while she types, not on blur: clearing on blur moves the button
       under her finger and the tap is lost */
    form.addEventListener('input', function (event) {
      var name = event.target.name;
      if (name === 'email' || name === 'first_name' || name === 'consent') mailStatus('');
    });

    form.addEventListener('click', function (event) {
      var button = event.target.closest('button');
      if (!button || button.disabled) return;
      if (button.hasAttribute('data-zbq-next')) {
        event.preventDefault();
        next();
      } else if (button.hasAttribute('data-zbq-back')) {
        event.preventDefault();
        back();
      } else if (button.hasAttribute('data-zbq-finish')) {
        event.preventDefault();
        fromEmailStep(button);
      } else if (button.hasAttribute('data-zbq-skip')) {
        event.preventDefault();
        finish('');
      }
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (state.current === 'email') fromEmailStep(form.querySelector('[data-zbq-finish]'));
      else next();
    });

    /* ---------- Her shelf ---------- */

    function recommend() {
      var a = state.a;
      var c = a.concerns;
      var score = {};
      var why = {};
      var tags = {};
      ORDER.forEach(function (o) {
        score[o.h] = 0;
        tags[o.h] = [];
      });
      function add(handle, points, lineKey, tag) {
        score[handle] += points;
        if (lineKey && (!why[handle] || points > why[handle].points)) why[handle] = { key: lineKey, points: points };
        if (!tag) return;
        var known = tags[handle].filter(function (t) {
          return t.tag === tag;
        })[0];
        if (known) known.points += points;
        else tags[handle].push({ tag: tag, points: points, at: tags[handle].length });
      }

      if (has(c, 'lines')) add(NR, 3, 'lines', TAG.concerns.lines);
      if (a.goal === 'lines') add(NR, 3, 'lines', TAG.goal.lines);
      if (has(c, 'dry')) {
        add(HA, 3, 'dry', TAG.concerns.dry);
        add(NR, 1, 'lid');
      }
      if (a.type === 'dry') add(HA, 2, 'dry', TAG.type.dry);
      if (a.goal === 'glow') add(HA, 3, 'dry', TAG.goal.glow);
      if (has(c, 'tone')) add(TINT, 3, 'tone', TAG.concerns.tone);
      if (a.goal === 'even') {
        add(TINT, 2, 'tone', TAG.goal.even);
        add(NR, 2, 'bright', TAG.goal.even);
      }
      if (has(c, 'pores')) {
        add(NIA, 3, 'pores', TAG.concerns.pores);
        add(OF, 2, 'shine', TAG.concerns.pores);
        add(CO, 1, 'oil', TAG.concerns.pores);
      }
      if (a.type === 'oily' || a.type === 'combo') {
        add(OF, 2, 'shine', TAG.type[a.type]);
        add(NIA, 1, 'pores', TAG.type[a.type]);
      }
      if (a.goal === 'clear') {
        add(NIA, 2, 'pores', TAG.goal.clear);
        add(OF, 1, 'shine', TAG.goal.clear);
        add(CO, 1, 'oil', TAG.goal.clear);
      }
      if (has(c, 'redness')) add(NIA, 3, 'calm', TAG.concerns.redness);
      if (a.goal === 'calm') add(NIA, 3, 'calm', TAG.goal.calm);
      if (has(c, 'lips')) add(LB, 4, 'lips', TAG.concerns.lips);
      if (has(c, 'keep')) {
        add(CO, 1, 'base', TAG.concerns.keep);
        add(NIA, 1, 'base', TAG.concerns.keep);
      }
      if (a.finish === 'tint') add(TINT, 4, 'light', TAG.finish.tint);
      if (a.finish === 'makeup') add(MR, 3, 'makeup', TAG.finish.makeup);
      if (a.finish === 'bare') add(OF, 1, 'base', TAG.finish.bare);
      if (a.climate === 'dry' || a.climate === 'mountain') {
        add(HA, 1, 'dry', TAG.climate[a.climate]);
        add(LB, 1, 'lips', TAG.climate[a.climate]);
      }
      if (a.climate === 'humid') {
        add(OF, 1, 'shine', TAG.climate.humid);
        add(NIA, 1, 'pores', TAG.climate.humid);
      }
      if (a.retinoid === 'yes') add(NR, 3, 'lines', 'yes to a retinoid');
      if (a.retinoid === 'unsure') add(NR, 1, 'lines');

      /* Night Renewal "trades gentleness for results" (its page), and The Full Edit page says it
         is "not a sensitive-skin product". For redness or sensitivity, or "very sensitive" or
         "too strong" at question 9, it isn't ticked: it waits in the rest of the shelf with that
         reason, so she can still add it. For teens it isn't shown at all. */
      var gentle = a.sensitive === 'yes' || has(c, 'redness') || a.why === 'sensitive' || a.why === 'strong';
      var teen = a.age === 'teen';
      var nrReason = teen ? 'teen' : gentle ? 'gentle' : a.retinoid === 'no' ? 'no' : '';
      var shown = {};
      ORDER.forEach(function (o) {
        shown[o.h] = available(o.h);
      });
      if (teen) shown[NR] = false;
      var nrTick = shown[NR] && !nrReason;
      /* "Deeper than these": our tint stops at Maple, so it isn't ticked. It waits in the rest of
         the shelf, with a kind line, in case she wants it anyway. */
      var noTint = a.skintone === 'deeper';

      /* Her shelf: the basics for a full morning and evening, then everything her answers point
         to. No cap. Hyaluronic comes with Night Renewal as its lid, as its page says. */
      var picks = [];
      function put(handle) {
        if (noTint && handle === TINT) return;
        if (shown[handle] && !has(picks, handle)) picks.push(handle);
      }
      [CO, OF, MR].forEach(put);
      if (score[NIA] >= 2) put(NIA);
      if (score[HA] >= 2) {
        put(HA);
        if (nrTick) put(NR);
      }
      if (nrTick && score[NR] >= 2) put(NR);
      if (score[TINT] >= 2 && a.finish !== 'makeup') put(TINT);
      if (score[LB] >= 2) put(LB);
      picks.sort(byRoutine);

      /* The size of her shelf is her choice ("How many steps would you like?"): two (cleanse and
         moisturize), three or four, or everything that fits her. The rest stays below, unticked. */
      var fit = picks.slice();
      if (a.steps === 'two' || a.steps === 'few') {
        picks = [];
        put(shown[CO] ? CO : MR);
        put(shown[OF] ? OF : TINT);
      }
      if (a.steps === 'few') {
        var extras = fit
          .filter(function (h) {
            return !has(picks, h) && score[h] >= 2;
          })
          .sort(function (x, y) {
            return score[y] - score[x] || indexOf(x) - indexOf(y);
          })
          .slice(0, 2);
        /* Hyaluronic comes with Night Renewal as its lid, as its page says */
        if (has(extras, HA) && has(fit, NR)) extras = [HA, NR];
        extras.forEach(put);
        if (picks.length < 3) put(MR);
      }
      picks.sort(byRoutine);

      /* Everything else stays in view, unticked, so she can add any of it */
      var rest = ORDER.map(function (o) {
        return o.h;
      }).filter(function (h) {
        return shown[h] && !has(picks, h);
      });

      if (has(picks, OF) && has(picks, TINT) && !why[OF]) why[OF] = { key: 'tint', points: 0 };
      if (has(picks, NR) && has(picks, HA) && (!why[NR] || why[NR].key === 'lid')) why[NR] = { key: 'lid', points: 0 };
      if (nrReason === 'gentle') why[NR] = { key: 'gentle', points: 99 };

      var fe = data.edit;
      state.picks = picks;
      state.rest = rest;
      state.why = why;
      state.tags = tags;
      state.nrReason = nrReason;
      state.noTint = noTint;
      state.leadEdit = !!(fe && fe.available && !noTint && (a.steps === 'full' || total(picks, true) >= fe.price));
    }

    /* Her face answer becomes her shade, unless she has since picked one herself on the result */
    function matchShade() {
      var a = state.a;
      var tone = a && asksTone(a) ? a.skintone : '';
      state.matchOut = '';
      if (!tone || tone === 'deeper' || tone === 'unsure') {
        if (state.shadeFrom === 'face') {
          state.shade = '';
          state.shadeFrom = '';
        }
        return;
      }
      if (state.shade && state.shadeFrom !== 'face') return;
      var p = byHandle[TINT];
      var match = p && (p.variants || []).filter(function (v) {
        return v.title === tone;
      })[0];
      if (match && match.available) {
        state.shade = match.title;
        state.shadeFrom = 'face';
      } else {
        state.shade = '';
        state.shadeFrom = '';
        state.matchOut = tone;
      }
    }

    function shadeLabel() {
      if (!state.shade) return 'Choose your shade';
      return 'Your shade: ' + state.shade + (state.shadeFrom === 'face' ? ' · matched from your answer' : '');
    }

    function lineFor(handle) {
      var w = state.why[handle];
      var key = w && LINE[handle][w.key] ? w.key : FALLBACK[handle];
      if (handle === OF && has(state.picks, TINT) && (!w || w.key === 'base')) key = 'tint';
      return LINE[handle][key];
    }

    /* Her two strongest reasons, without saying the same thing twice */
    function forText(handle) {
      if (handle === TINT && state.noTint) return 'Our shades go as deep as Maple for now';
      var tags = [];
      (state.tags[handle] || [])
        .slice()
        .sort(function (x, y) {
          return y.points - x.points || x.at - y.at;
        })
        .forEach(function (t) {
          if (tags.length >= 2) return;
          var repeat = tags.some(function (seen) {
            return seen.indexOf(t.tag) !== -1 || t.tag.indexOf(seen) !== -1;
          });
          if (!repeat) tags.push(t.tag);
        });
      return tags.length ? 'Because you said ' + tags.join(' · ') : ROLE[handle];
    }

    /* Steps counted within her own list, so her morning reads 1, 2, 3 */
    function stepLabels(list, numbered) {
      var am = 0;
      var pm = 0;
      var out = {};
      list
        .slice()
        .sort(byRoutine)
        .forEach(function (h) {
          var part = ORDER[indexOf(h)].part;
          if (h === TINT) out[h] = 'Morning · after sunscreen';
          else if (part === 'am') out[h] = numbered ? 'Morning · step ' + ++am : 'Morning';
          else if (part === 'pm') out[h] = numbered ? 'Evening · step ' + ++pm : 'Evening';
          else out[h] = 'Whenever you like';
        });
      return out;
    }

    function total(list, afterDiscount) {
      var subtotal = 0;
      var off = 0;
      list.forEach(function (handle) {
        var price = byHandle[handle] ? byHandle[handle].price : 0;
        subtotal += price;
        off += Math.round((price * routine.percent) / 100);
      });
      var deal = routine.percent > 0 && list.length >= routine.min;
      return afterDiscount && deal ? subtotal - off : subtotal;
    }

    /* ---------- How to start: her notes only ---------- */

    function notesFor() {
      var a = state.a;
      var shelf = state.picks;
      var list = ['Finish every morning with your sunscreen, after the moisturizer and before any tint.'];
      if (has(shelf, NR)) {
        list.push('Night Renewal Creme is the last step of your evening. A small amount goes a long way.');
        list.push('Wear SPF the day after Night Renewal. It has glycolic acid and a retinoid, and both increase sun sensitivity.');
        if (a.using === 'yes') list.push('You use a retinoid now, so use Night Renewal in place of it, not on top.');
        if (a.why === 'strength') list.push('Night Renewal Creme comes in one strength.');
        list.push('If you see a dermatologist or esthetician, show them your shelf before you start Night Renewal.');
      }
      if (has(shelf, HA)) list.push('Hyaluronic Acid Serum goes on skin still damp from cleansing: a few drops, patted in.');
      if (state.nrReason === 'gentle') {
        list.push('We haven’t ticked Night Renewal Creme. It’s the one product on our shelf that trades gentleness for results, so it’s your call. It’s in the rest of the shelf.');
      }
      list.push('Trying something new? Patch-test on the inside of your wrist first.');
      return list;
    }

    /* ---------- The result ---------- */

    function toldHTML() {
      var a = state.a;
      var tags = [];
      if (a.type) tags.push(TAG.type[a.type]);
      a.concerns.slice(0, 3).forEach(function (v) {
        tags.push(TAG.concerns[v]);
      });
      if (a.sensitive === 'yes') tags.push('prone to redness or sensitivity');
      if (a.finish) tags.push(TAG.finish[a.finish]);
      if (a.climate) tags.push(TAG.climate[a.climate]);
      if (a.steps) tags.push(TAG.steps[a.steps]);
      return (
        '<div class="zbq-told"><p class="zbq-told__name">What you told us</p><ul>' +
        tags
          .map(function (t) {
            return '<li>' + esc(cap(t)) + '</li>';
          })
          .join('') +
        '</ul></div>'
      );
    }

    /* Shades listed light to deep, the same order as the faces. Discontinued ones are left out. */
    function shadeSorted(variants) {
      var rank = {};
      ((data.edit && data.edit.variants) || []).forEach(function (v, i) {
        rank[v.title] = 100 + i;
      });
      SHADES.forEach(function (title, i) {
        rank[title] = i;
      });
      return variants
        .filter(function (v) {
          return !has(GONE, v.title);
        })
        .sort(function (a, b) {
          return (a.title in rank ? rank[a.title] : 999) - (b.title in rank ? rank[b.title] : 999);
        });
    }

    /* The shade, picked by its own packshot */
    function shadeChips(variants, id) {
      return shadeSorted(variants)
        .map(function (v) {
          return (
            '<label class="zbq-shade' +
            (v.available ? '' : ' is-out') +
            '"><input type="radio" name="' +
            id +
            '-shade" value="' +
            esc(v.title) +
            '" data-zbq-shade' +
            (v.available ? '' : ' disabled') +
            (state.shade === v.title ? ' checked' : '') +
            '><span class="zbq-shade__img">' +
            (v.img ? '<img src="' + esc(v.img) + '" alt="" width="64" height="64" loading="lazy">' : '') +
            '</span><span class="zbq-shade__name">' +
            esc(v.title) +
            '</span>' +
            (v.available ? '' : '<span class="zbq-shade__out">Sold out</span>') +
            '</label>'
          );
        })
        .join('');
    }

    function shadeOptions(variants) {
      var html = '<option value="">Choose your shade</option>';
      shadeSorted(variants)
        .forEach(function (v) {
          html +=
            '<option value="' +
            esc(v.title) +
            '"' +
            (v.available ? '' : ' disabled') +
            (state.shade === v.title ? ' selected' : '') +
            '>' +
            esc(v.title) +
            (v.available ? '' : ' (sold out)') +
            '</option>';
        });
      return html;
    }

    function itemHTML(handle, on, label) {
      var p = byHandle[handle];
      if (!p) return '';
      var o = ORDER[indexOf(handle)];
      var id = 'zbq-' + handle;
      var icon = o.part === 'am' ? SUN : o.part === 'pm' ? MOON : DOT;
      var img = p.img
        ? '<img class="zbq-item__img" src="' +
          esc(p.img) +
          '" srcset="' +
          esc(p.img) +
          ' 160w, ' +
          esc(p.img2) +
          ' 320w" sizes="64px" width="64" height="64" alt="" loading="lazy">'
        : '<span class="zbq-item__img"></span>';
      var extra = '';
      if (handle === TINT) {
        extra =
          '<div class="zbq-item__extra" data-zbq-shade-row' +
          (on ? '' : ' hidden') +
          '><p class="zbq-shades__label" id="' +
          id +
          '-shades" data-zbq-shade-name>' +
          esc(shadeLabel()) +
          '</p>' +
          (state.matchOut
            ? '<p class="zbq-shades__note">Your match, ' + esc(state.matchOut) + ', is sold out right now. Choose another shade, or check back soon.</p>'
            : '') +
          '<div class="zbq-shades" role="radiogroup" aria-labelledby="' +
          id +
          '-shades">' +
          shadeChips(p.variants || [], id) +
          '</div><p class="zbq-shades__tip" data-zbq-tip' +
          (has(DEEP_THREE, state.shade) ? '' : ' hidden') +
          '>' +
          esc(TIP) +
          '</p><a class="zbq-link zbq-item__more" href="' +
          esc(p.url) +
          '">Not sure? Find your shade</a></div>';
      }
      return (
        '<li class="zbq-item' +
        (on ? ' is-on' : '') +
        '" data-zbq-item="' +
        esc(handle) +
        '"><label class="zbq-item__pick"><input type="checkbox" value="' +
        esc(handle) +
        '"' +
        (on ? ' checked' : '') +
        (p.available ? '' : ' disabled') +
        '>' +
        img +
        '<span class="zbq-item__text"><span class="zbq-item__step">' +
        icon +
        esc(label || o.step) +
        '</span><span class="zbq-item__name">' +
        esc(p.title) +
        '</span><span class="zbq-item__for">' +
        esc(forText(handle)) +
        '</span><span class="zbq-item__why">' +
        esc(p.available ? lineFor(handle) : 'Sold out for now.') +
        '</span></span></label><span class="zbq-item__side"><span class="zbq-item__price">' +
        money(p.price) +
        '</span><a class="zbq-link zbq-item__more" href="' +
        esc(p.url) +
        '">Details<span class="zbq-hide"> on ' +
        esc(p.title) +
        '</span></a></span>' +
        extra +
        '</li>'
      );
    }

    function blockHTML(key, name, note, list, on) {
      var labels = stepLabels(list, on);
      return (
        '<section class="zbq-block zbq-block--' +
        key +
        '" aria-label="' +
        esc(name) +
        '"><div class="zbq-block__head"><h4 class="zbq-block__name">' +
        esc(name) +
        '</h4><p class="zbq-block__note">' +
        esc(note) +
        '</p></div><ul class="zbq-items">' +
        list
          .map(function (h) {
            return itemHTML(h, on, labels[h]);
          })
          .join('') +
        '</ul></section>'
      );
    }

    function editHTML(lead) {
      var fe = data.edit;
      if (!fe || !fe.available) return '';
      var compare = fe.compare && fe.compare > fe.price ? ' <s>' + money(fe.compare) + '</s>' : '';
      var singles = 0;
      (data.products || []).forEach(function (p) {
        singles += p.price;
      });
      var cheaper = singles > fe.price ? ', and they cost less together than one by one' : '';
      var text = !lead
        ? 'All eight, both routines, in one box. Choose your tint shade and it’s done.'
        : state.a && state.a.steps === 'full'
          ? 'You asked for the full shelf: all eight, both routines, in one box' + cheaper + '. Choose your tint shade and it’s done.'
          : 'Your answers point to most of the shelf' + cheaper.replace(', and they', ', and all eight') + '. Choose your tint shade and it’s done.';
      if (state.nrReason === 'gentle') text += ' It includes Night Renewal Creme, which isn’t a sensitive-skin product.';
      if (state.noTint) text += ' Its tint goes as deep as Maple.';
      return (
        '<div class="zbq-fe' +
        (lead ? ' zbq-fe--lead' : '') +
        '"><div class="zbq-fe__top">' +
        (fe.img ? '<img class="zbq-fe__img" src="' + esc(fe.img) + '" alt="" loading="lazy" width="96" height="96">' : '') +
        '<div class="zbq-fe__name"><p class="zbq-eyebrow">' +
        (lead ? 'Your shelf, all of it' : 'Or the whole shelf') +
        '</p><p class="zbq-fe__title">' +
        esc(fe.title) +
        '</p><p class="zbq-fe__price">' +
        money(fe.price) +
        compare +
        '</p></div></div><p class="zbq-fe__text">' +
        esc(text) +
        /* Her shade, when she has one, so she can see The Full Edit's tint is already set */
        '</p><p class="zbq-shades__label zbq-fe__shade" data-zbq-fe-shade' +
        (state.shade ? '' : ' hidden') +
        '>' +
        esc(shadeLabel()) +
        '</p><div class="zbq-fe__row"><label class="zbq-hide" for="zbq-fe-shade">Tint shade for The Full Edit</label><select class="zbq-select" id="zbq-fe-shade" data-zbq-shade>' +
        shadeOptions(fe.variants || []) +
        '</select><button type="button" class="button" data-zbq-add-edit>Add ' +
        esc(fe.title) +
        '</button></div><p class="zbq-status" role="status" data-zbq-edit-status></p><a class="zbq-link" href="' +
        esc(fe.url) +
        '">What’s inside</a></div>'
      );
    }

    function mailHTML(mail) {
      var preview = isLive() ? '' : '<p class="zbq-preview">Preview only: nothing was sent to Klaviyo.</p>';
      if (state.emailed) {
        return (
          '<div class="zbq-mail zbq-mail--done"><p class="zbq-block__name">Your shelf is on its way.</p><p class="zbq-block__note">Check your inbox in a minute or two.</p>' +
          preview +
          '</div>'
        );
      }
      return (
        '<form class="zbq-mail" data-zbq-mail novalidate><p class="zbq-block__name">Email me my shelf</p><p class="zbq-block__note">' +
        (mail === 'failed' ? 'That didn’t go through. Please try once more.' : 'Your shelf, in order, in your inbox for when you need it.') +
        '</p><div class="zbq-mail__row"><label class="zbq-hide" for="zbq-mail-email">Email</label><input class="zbq-input" id="zbq-mail-email" type="email" name="mail_email" autocomplete="email" inputmode="email" maxlength="120" placeholder="Email address"><button type="submit" class="button" data-zbq-mail-send>Send</button></div><label class="zbq-consent"><input type="checkbox" name="mail_consent" value="yes"><span>Email me my shelf, plus Zobyda news and offers. Unsubscribe anytime.</span></label><p class="zbq-status" role="status" data-zbq-mail-status2></p></form>'
      );
    }

    function renderResult(mail) {
      var a = state.a;
      var n = state.picks.length;
      var title = state.name ? esc(state.name) + ', here’s your <em>shelf.</em>' : 'Here’s your <em>shelf.</em>';
      var html =
        '<div class="zbq-result__head"><p class="zbq-eyebrow">Your shelf</p><h3 class="zbq-result__title">' +
        title +
        '</h3><p class="zbq-result__sum">' +
        esc((GOAL[a.goal] ? GOAL[a.goal] + ' ' : '') + n + ' products, in the order you’ll use them.') +
        '</p>' +
        toldHTML() +
        /* Only after "A hint of tint": a makeup shelf never has the tint, so there's nothing to explain */
        (state.noTint && a.finish === 'tint' ? '<p class="zbq-result__note">' + esc(NO_TINT) + '</p>' : '') +
        '</div><div class="zbq-strip" data-zbq-strip aria-hidden="true"></div>';
      if (state.leadEdit) html += editHTML(true);
      html += blockHTML('shelf', 'Your shelf', 'Ticked and ready. Untick anything you already have.', state.picks, true);
      html +=
        '<div class="zbq-total"><p class="zbq-total__line"><span class="zbq-total__count" data-zbq-count></span><span class="zbq-total__price" data-zbq-price></span></p><p class="zbq-total__note" data-zbq-offer></p><button type="button" class="button" data-zbq-add>Add my shelf</button><p class="zbq-total__promise">Free US shipping · 30 days to try it, even opened</p><p class="zbq-status" role="status" data-zbq-status></p></div>';
      if (state.rest.length) {
        html += blockHTML('rest', 'The rest of the shelf', 'Not on your shelf yet. Tick any to add them.', state.rest, false);
      }
      html +=
        '<section class="zbq-start" aria-label="How to start"><h4 class="zbq-block__name">How to start</h4><ul>' +
        notesFor()
          .map(function (t) {
            return '<li>' + esc(t) + '</li>';
          })
          .join('') +
        '</ul></section>';
      html += mailHTML(mail);
      if (!state.leadEdit) html += editHTML(false);
      html +=
        '<div class="zbq-result__foot"><p>A starting point, not a prescription. Change anything above.</p><button type="button" class="zbq-link" data-zbq-restart>Start again</button></div>';
      resultBox.innerHTML = html;
      updateTotal();
    }

    function chosen() {
      var list = [];
      resultBox.querySelectorAll('.zbq-item input[type="checkbox"]:checked').forEach(function (input) {
        list.push(input.value);
      });
      return list;
    }

    function updateTotal() {
      var list = chosen();
      var subtotal = total(list, false);
      var now = total(list, true);
      var deal = now < subtotal;
      var count = resultBox.querySelector('[data-zbq-count]');
      var price = resultBox.querySelector('[data-zbq-price]');
      var offer = resultBox.querySelector('[data-zbq-offer]');
      var add = resultBox.querySelector('[data-zbq-add]');
      if (!count) return;
      count.textContent = list.length === 1 ? '1 product' : list.length + ' products';
      price.innerHTML = list.length ? (deal ? '<s>' + money(subtotal) + '</s>' : '') + money(now) : '';
      var text = '';
      if (deal) {
        text = routine.percent + '% comes off in your bag. No code needed.';
      } else if (list.length && list.length === routine.min - 1 && routine.percent > 0) {
        text = 'Add one more and ' + routine.percent + '% comes off in your bag.';
      }
      var fe = data.edit;
      if (fe && fe.available && now > fe.price) {
        text += (text ? ' ' : '') + 'All eight cost less as ' + fe.title + (state.leadEdit ? ', above.' : ', below.');
      }
      offer.textContent = text;
      add.disabled = !list.length;
      add.textContent = list.length ? 'Add my shelf · ' + money(now) : 'Tick at least one product';
      var strip = resultBox.querySelector('[data-zbq-strip]');
      if (strip) {
        strip.innerHTML = list
          .slice()
          .sort(byRoutine)
          .map(function (h) {
            var p = byHandle[h];
            return p && p.img2 ? '<img src="' + esc(p.img2) + '" alt="" width="96" height="96" loading="lazy">' : '';
          })
          .join('');
        strip.hidden = !list.length;
      }
      resultBox.querySelectorAll('.zbq-item').forEach(function (row) {
        var on = has(list, row.getAttribute('data-zbq-item'));
        row.classList.toggle('is-on', on);
        var shadeRow = row.querySelector('[data-zbq-shade-row]');
        if (shadeRow) shadeRow.hidden = !on;
      });
    }

    function remember() {
      save({ a: state.a, name: state.name, shade: state.shade, shadeFrom: state.shadeFrom, emailed: state.emailed, chosen: chosen() });
    }

    function compute() {
      state.a = readAnswers();
      recommend();
      matchShade();
    }

    function finish(mail) {
      compute();
      if (mail === 'sent') state.emailed = true;
      renderResult(mail);
      show('result');
      remember();
      if (state.a.age !== 'teen') {
        publish('zb_quiz_finished', {
          count: state.picks.length,
          value: dollars(total(state.picks, true)),
          currency: data.currency || 'USD'
        });
      }
    }

    /* ---------- Email: optional, with consent ---------- */

    function mailStatus(text) {
      var status = form.querySelector('[data-zbq-mail-status]');
      if (status) status.textContent = text;
    }

    function checkMail(email, consent) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return 'That email doesn’t look quite right.';
      if (!consent) return 'Tick the box so we can email your shelf, or skip this step.';
      return '';
    }

    function variantFor(handle) {
      var p = byHandle[handle];
      if (!p || !p.variants || !p.variants.length) return null;
      if (handle === TINT) {
        for (var i = 0; i < p.variants.length; i++) {
          if (p.variants[i].title === state.shade && p.variants[i].available) return p.variants[i].id;
        }
        return null;
      }
      for (var j = 0; j < p.variants.length; j++) if (p.variants[j].available) return p.variants[j].id;
      return null;
    }

    function itemInfo(handle, labels) {
      var p = byHandle[handle];
      return {
        title: p.title,
        url: abs(p.url),
        image: abs(p.img2 || p.img || ''),
        price: dollars(p.price),
        step: (labels && labels[handle]) || ORDER[indexOf(handle)].step,
        because: forText(handle),
        line: lineFor(handle)
      };
    }

    /* The tint rides along only when her shade is known; otherwise the email asks her to choose it */
    function cartLink(list) {
      var parts = list
        .map(function (h) {
          var id = variantFor(h);
          return id ? id + ':1' : '';
        })
        .filter(Boolean);
      return parts.length ? abs(root + 'cart/' + parts.join(',')) : '';
    }

    function profileProps() {
      var a = state.a;
      function text(name) {
        return a[name] ? answerText(name, a[name]) : '';
      }
      var props = {
        'Quiz skin concerns': a.concerns.map(function (v) {
          return answerText('concerns', v);
        }),
        'Quiz skin type': text('type'),
        'Quiz goal': text('goal'),
        'Quiz prone to redness or sensitivity': text('sensitive'),
        'Quiz age range': text('age'),
        'Quiz retinoid interest': text('retinoid'),
        'Quiz retinoid now': text('using'),
        'Quiz morning finish': text('finish'),
        'Quiz climate': text('climate'),
        'Quiz steps wanted': text('steps'),
        'Quiz your shelf': state.picks.map(function (h) {
          return byHandle[h].title;
        }),
        'Quiz completed': new Date().toISOString()
      };
      if (a.why) props['Quiz retinoid reason'] = text('why');
      /* Only the shade name, never the skin-tone answer itself */
      if (state.shade) props['Quiz tint shade'] = state.shade;
      return props;
    }

    function eventProps() {
      return {
        FirstName: state.name || '',
        Shelf: state.picks.map(function (h) {
          return itemInfo(h, stepLabels(state.picks, true));
        }),
        Rest: state.rest.map(function (h) {
          return itemInfo(h, stepLabels(state.rest, false));
        }),
        ShelfValue: dollars(total(state.picks, false)),
        ShelfValueAfterDiscount: dollars(total(state.picks, true)),
        CartLink: cartLink(state.picks),
        TintOnShelf: has(state.picks, TINT) && !variantFor(TINT),
        TintShade: has(state.picks, TINT) ? state.shade : '',
        HowToStart: notesFor(),
        Goal: state.a.goal ? answerText('goal', state.a.goal) : ''
      };
    }

    function lead() {
      if (state.a && state.a.age !== 'teen') publish('zb_quiz_lead', {});
      return true;
    }

    function subscribe(email) {
      var k = data.klaviyo || {};
      var profile = profileProps();
      var event = eventProps();
      if (!isLive()) {
        if (window.console && window.console.info) {
          window.console.info('[Find your shelf] Preview: nothing sent to Klaviyo. On the live site this would send:', {
            list: k.list,
            profile: profile,
            event: event
          });
        }
        return Promise.resolve(lead());
      }
      if (!k.key || !k.list || !window.fetch) return Promise.resolve(false);
      var base = 'https://a.klaviyo.com/client/';
      var query = '?company_id=' + encodeURIComponent(k.key);
      var headers = {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      };
      function post(path, body) {
        return fetch(base + path + query, { method: 'POST', headers: headers, body: JSON.stringify(body) }).then(
          function (response) {
            if (!response.ok) throw new Error(path + ' ' + response.status);
            return true;
          }
        );
      }
      var subscription = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Find your shelf quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: k.list } } }
        }
      };
      var profileBody = { data: { type: 'profile', attributes: { email: email, properties: profile } } };
      if (state.name) profileBody.data.attributes.first_name = state.name;
      var eventBody = {
        data: {
          type: 'event',
          attributes: {
            properties: event,
            metric: { data: { type: 'metric', attributes: { name: KLAVIYO_EVENT } } },
            profile: { data: { type: 'profile', attributes: { email: email } } },
            value: event.ShelfValueAfterDiscount,
            value_currency: data.currency || 'USD',
            unique_id: 'zbq-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
          }
        }
      };
      return post('subscriptions/', subscription)
        .then(function () {
          return post('profiles/', profileBody).catch(noop);
        })
        .then(function () {
          return post('events/', eventBody).catch(noop);
        })
        .then(function () {
          try {
            if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email: email });
          } catch (error) {
            /* optional */
          }
          return lead();
        })
        .catch(function () {
          return false;
        });
    }

    function busy(button, on, text) {
      if (!button) return;
      if (on) {
        button.setAttribute('data-label', button.textContent);
        button.textContent = text;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else {
        button.textContent = button.getAttribute('data-label') || button.textContent;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    function fromEmailStep(button) {
      var email = (form.elements.email.value || '').trim();
      var name = (form.elements.first_name.value || '').trim();
      if (!email) {
        state.name = name;
        finish('');
        return;
      }
      var problem = checkMail(email, form.elements.consent.checked);
      if (problem) {
        mailStatus(problem);
        return;
      }
      state.name = name;
      compute();
      busy(button, true, 'One moment…');
      subscribe(email).then(function (ok) {
        busy(button, false);
        finish(ok ? 'sent' : 'failed');
      });
    }

    /* ---------- Adding to the bag (same flow as the product pages) ---------- */

    function itemCountFrom(html) {
      if (!html) return null;
      var node = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
      var n = node ? parseInt(node.textContent, 10) : NaN;
      return isNaN(n) ? null : n;
    }

    function openDrawer() {
      var drawer = document.querySelector('cart-drawer-component');
      if (!drawer) {
        window.location.href = root + 'cart';
        return;
      }
      var dialog = drawer.querySelector('dialog');
      if (dialog && dialog.open) return;
      if (typeof drawer.open === 'function') drawer.open();
      else if (typeof drawer.showDialog === 'function') drawer.showDialog();
    }

    /* Her answers ride along on the order (Additional details), so you can see which answers buy */
    function noteOnCart() {
      var a = state.a;
      if (!a) return Promise.resolve();
      var parts = [];
      function addPart(label, name) {
        if (a[name]) parts.push(label + ': ' + answerText(name, a[name]));
      }
      if (a.concerns.length) {
        parts.push(
          'Concerns: ' +
            a.concerns
              .map(function (v) {
                return answerText('concerns', v);
              })
              .join(', ')
        );
      }
      addPart('Skin', 'type');
      addPart('Goal', 'goal');
      addPart('Redness or sensitivity', 'sensitive');
      addPart('Age', 'age');
      addPart('Retinoid', 'retinoid');
      addPart('Retinoid now', 'using');
      addPart('Why not', 'why');
      addPart('Morning', 'finish');
      addPart('Climate', 'climate');
      addPart('Steps', 'steps');
      var attributes = {
        'Find your shelf': parts.join(' · '),
        'Find your shelf picks': state.picks
          .map(function (h) {
            return byHandle[h].title;
          })
          .join(', ')
      };
      return fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).catch(noop);
    }

    function addToBag(items) {
      var host = document.querySelector('cart-items-component[data-drawer]');
      var sectionId = host ? host.dataset.sectionId : null;
      var body = { items: items };
      if (sectionId) {
        body.sections = sectionId;
        body.sections_url = window.location.pathname;
      }
      return noteOnCart()
        .then(function () {
          return fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          });
        })
        .then(function (response) {
          return response.json().then(function (json) {
            if (!response.ok) {
              var error = new Error('add failed');
              error.data = json;
              throw error;
            }
            return json;
          });
        })
        .then(function (json) {
          var sections = json.sections || {};
          var count = itemCountFrom(sectionId ? sections[sectionId] : null);
          var ready =
            count !== null
              ? Promise.resolve(count)
              : fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
                  .then(function (r) {
                    return r.json();
                  })
                  .then(function (cart) {
                    return cart.item_count;
                  });
          return ready.then(function (itemCount) {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: json,
                  sourceId: 'zb-shelf',
                  data: { itemCount: itemCount, source: 'zb-shelf', sections: sections }
                }
              })
            );
            openDrawer();
            return json;
          });
        });
    }

    function run(button, status, items, done) {
      var label = button.textContent;
      var restore = function () {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (button.hasAttribute('data-zbq-add')) updateTotal();
        else button.textContent = label;
      };
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
      status.textContent = '';
      addToBag(items)
        .then(function () {
          button.textContent = 'Added';
          status.textContent = 'Added to your bag.';
          if (done) done();
          window.setTimeout(function () {
            restore();
            status.textContent = '';
          }, 2400);
        })
        .catch(function (error) {
          restore();
          status.textContent =
            error && error.data && error.data.description ? error.data.description : 'That didn’t go through. Please try again.';
        });
    }

    function syncShade() {
      resultBox.querySelectorAll('select[data-zbq-shade]').forEach(function (select) {
        select.value = state.shade;
        select.classList.remove('is-missing');
      });
      resultBox.querySelectorAll('input[data-zbq-shade]').forEach(function (input) {
        input.checked = input.value === state.shade;
      });
      resultBox.querySelectorAll('.zbq-shades').forEach(function (box) {
        box.classList.remove('is-missing');
      });
      resultBox.querySelectorAll('[data-zbq-shade-name]').forEach(function (label) {
        label.textContent = shadeLabel();
      });
      resultBox.querySelectorAll('[data-zbq-fe-shade]').forEach(function (label) {
        label.textContent = shadeLabel();
        label.hidden = !state.shade;
      });
      resultBox.querySelectorAll('[data-zbq-tip]').forEach(function (tip) {
        tip.hidden = !has(DEEP_THREE, state.shade);
      });
    }

    function needShade(status) {
      status.textContent = 'Choose your Tinted Moisturizer shade first.';
      var chips = resultBox.querySelector('.zbq-item .zbq-shades');
      if (chips) {
        chips.classList.add('is-missing');
        var first = chips.querySelector('input:not([disabled])');
        if (first) first.focus();
        return;
      }
      var select = resultBox.querySelector('.zbq-item [data-zbq-shade]') || resultBox.querySelector('[data-zbq-shade]');
      if (select) {
        select.classList.add('is-missing');
        select.focus();
      }
    }

    function addChosen(button, status) {
      var list = chosen();
      var items = [];
      for (var i = 0; i < list.length; i++) {
        var id = variantFor(list[i]);
        if (!id) {
          if (list[i] === TINT) return needShade(status);
          continue;
        }
        items.push({ id: id, quantity: 1 });
      }
      if (!items.length) return;
      var value = dollars(total(list, true));
      run(button, status, items, function () {
        if (state.a && state.a.age !== 'teen') {
          publish('zb_quiz_add', { count: items.length, value: value, currency: data.currency || 'USD' });
        }
        remember();
      });
    }

    resultBox.addEventListener('change', function (event) {
      var target = event.target;
      if (target.matches('[data-zbq-shade]')) {
        state.shade = target.value;
        state.shadeFrom = '';
        state.matchOut = '';
        resultBox.querySelectorAll('.zbq-shades__note').forEach(function (note) {
          note.remove();
        });
        syncShade();
      }
      if (target.name === 'mail_email' || target.name === 'mail_consent') return;
      updateTotal();
      remember();
    });

    resultBox.addEventListener('input', function (event) {
      var name = event.target.name;
      if (name !== 'mail_email' && name !== 'mail_consent') return;
      var status = resultBox.querySelector('[data-zbq-mail-status2]');
      if (status) status.textContent = '';
    });

    resultBox.addEventListener('submit', function (event) {
      var mailForm = event.target.closest('[data-zbq-mail]');
      if (!mailForm) return;
      event.preventDefault();
      var status = mailForm.querySelector('[data-zbq-mail-status2]');
      var button = mailForm.querySelector('[data-zbq-mail-send]');
      var email = (mailForm.elements.mail_email.value || '').trim();
      var problem = checkMail(email, mailForm.elements.mail_consent.checked);
      if (problem) {
        status.textContent = problem;
        return;
      }
      busy(button, true, 'Sending…');
      subscribe(email).then(function (ok) {
        busy(button, false);
        if (!ok) {
          status.textContent = 'That didn’t go through. Please try once more.';
          return;
        }
        state.emailed = true;
        mailForm.outerHTML = mailHTML('sent');
        remember();
      });
    });

    resultBox.addEventListener('click', function (event) {
      var button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-zbq-restart')) {
        event.preventDefault();
        forget();
        form.reset();
        state = fresh();
        paintFace();
        mailStatus('');
        show('concerns');
        return;
      }
      if (button.hasAttribute('data-zbq-add')) {
        event.preventDefault();
        addChosen(button, resultBox.querySelector('[data-zbq-status]'));
        return;
      }
      if (button.hasAttribute('data-zbq-add-edit')) {
        event.preventDefault();
        var editStatus = resultBox.querySelector('[data-zbq-edit-status]');
        var fe = data.edit;
        var variant = null;
        (fe.variants || []).forEach(function (v) {
          if (v.title === state.shade && v.available) variant = v.id;
        });
        if (!variant) {
          editStatus.textContent = 'Choose your tint shade first.';
          var select = resultBox.querySelector('#zbq-fe-shade');
          if (select) {
            select.classList.add('is-missing');
            select.focus();
          }
          return;
        }
        run(button, editStatus, [{ id: variant, quantity: 1 }], function () {
          if (state.a && state.a.age !== 'teen') {
            publish('zb_quiz_add', { count: 1, value: dollars(fe.price), currency: data.currency || 'USD' });
          }
        });
      }
    });

    /* ---------- On phones, Back and Next stay at the bottom while the quiz is on screen ---------- */

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            el.classList.toggle('is-in-view', entry.isIntersecting);
          });
        },
        { rootMargin: '0px 0px -25% 0px' }
      ).observe(el);
    } else {
      el.classList.add('is-in-view');
    }

    /* ---------- Back with her earlier answers this visit ---------- */

    var saved = load();
    if (saved && saved.a && saved.a.goal && saved.a.steps) {
      Object.keys(saved.a).forEach(function (name) {
        var value = saved.a[name];
        var values = Array.isArray(value) ? value : [value];
        form.querySelectorAll('input[name="' + name + '"]').forEach(function (input) {
          if (input.type === 'radio' || input.type === 'checkbox') input.checked = has(values, input.value);
        });
      });
      state.name = saved.name || '';
      state.shade = saved.shade || '';
      state.shadeFrom = saved.shadeFrom || '';
      state.emailed = !!saved.emailed;
      paintFace();
      compute();
      renderResult(state.emailed ? 'sent' : '');
      if (saved.chosen) {
        resultBox.querySelectorAll('.zbq-item input[type="checkbox"]').forEach(function (input) {
          if (!input.disabled) input.checked = has(saved.chosen, input.value);
        });
        updateTotal();
      }
      show('result', true);
    } else {
      paintFace();
      show('concerns', true);
    }
  }

  function init() {
    document.querySelectorAll('[data-zbq]').forEach(function (el) {
      if (el.__zbq) return;
      el.__zbq = true;
      setup(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
